import { GRID_TAG, HUNG, OPERABLE, hungSplit } from "@/lib/design";

const INK = "#171A20", GOLD = "#A8823F";

// Arched / half-round top for a piece (same rise rules as the PDF drawing).
export function topRise(top, w, h) {
  if (top === "half_round") return Math.min(w / 2, h * 0.55);
  if (top === "arch") return Math.min(w * 0.2, h * 0.3);
  return 0;
}

export function piecePath(u) {
  const rise = topRise(u.node.top, u.w, u.h);
  if (!rise) return `M ${u.x} ${u.y} H ${u.x + u.w} V ${u.y + u.h} H ${u.x} Z`;
  const R = (u.w * u.w / 4 + rise * rise) / (2 * rise);
  return `M ${u.x} ${u.y + u.h} V ${u.y + rise} A ${R} ${R} 0 0 1 ${u.x + u.w} ${u.y + rise} V ${u.y + u.h} Z`;
}

export function gridBars(g, X1, Y1, X2, Y2) {
  const w = X2 - X1, h = Y2 - Y1, p = g.pattern || "colonial";
  if (p === "prairie") {
    const b = Math.min(Number(g.border) || 3, w / 3, h / 3);
    return [[X1 + b, Y1, X1 + b, Y2], [X2 - b, Y1, X2 - b, Y2], [X1, Y1 + b, X2, Y1 + b], [X1, Y2 - b, X2, Y2 - b]];
  }
  if (p === "custom") return (g.bars || []).map((b) => (b.dir === "v" ? [X1 + Number(b.at), Y1, X1 + Number(b.at), Y2] : [X1, Y1 + Number(b.at), X2, Y1 + Number(b.at)]));
  if (p === "diamond") {
    const n = Math.max(2, Number(g.cols) || 3), step = w / n, out = [];
    for (let i = -n * 3; i <= n * 3; i++) out.push([X1 + i * step, Y2, X1 + i * step + h, Y1], [X1 + i * step, Y1, X1 + i * step + h, Y2]);
    return out;
  }
  if (p === "sunburst") {
    const n = Math.max(3, Number(g.cols) || 5), cx = (X1 + X2) / 2, r = Math.max(w, h) * 1.5;
    return Array.from({ length: n - 1 }, (_, i) => [cx, Y2, cx + r * Math.cos((Math.PI * (i + 1)) / n), Y2 - r * Math.sin((Math.PI * (i + 1)) / n)]);
  }
  const rows = Math.max(1, Number(g.rows) || 2), cols = Math.max(1, Number(g.cols) || 2), out = [];
  for (let i = 1; i < cols; i++) out.push([X1 + (w * i) / cols, Y1, X1 + (w * i) / cols, Y2]);
  for (let i = 1; i < rows; i++) out.push([X1, Y1 + (h * i) / rows, X2, Y1 + (h * i) / rows]);
  return out;
}

function Bar({ b, type, testId, onDown }) {
  const [x1, y1, x2, y2] = b;
  const hit = onDown ? <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="4" style={{ cursor: "grab", touchAction: "none" }} onPointerDown={onDown} /> : null;
  const common = { x1, y1, x2, y2 };
  let art;
  if (type === "outside") art = <line {...common} stroke={INK} strokeWidth="1.1" />;
  else if (type === "inside") art = <line {...common} stroke={INK} strokeWidth="0.6" strokeDasharray="1.6 1" />;
  else if (type === "sdl") {
    const n = Math.hypot(x2 - x1, y2 - y1) || 1, ox = (-(y2 - y1) / n) * 0.45, oy = ((x2 - x1) / n) * 0.45;
    art = <><line x1={x1 + ox} y1={y1 + oy} x2={x2 + ox} y2={y2 + oy} stroke={INK} strokeWidth="0.35" />
      <line x1={x1 - ox} y1={y1 - oy} x2={x2 - ox} y2={y2 - oy} stroke={INK} strokeWidth="0.35" /></>;
  } else art = <line {...common} stroke={GOLD} strokeWidth="0.45" />;
  return <g data-testid={testId} style={{ pointerEvents: onDown ? "auto" : "none" }}>{art}{hit}</g>;
}

// Fill (glass / solid panel), grid by type and pattern, and handle mark of one section (inches, y down).
export function SectionArt({ s, idx, clipId, onBarDown }) {
  const { x, y, w, h } = s;
  const node = { ...s.node, grid: s.node.grid && typeof s.node.grid === "object" ? s.node.grid : s.node.grid === true ? { type: "btg", pattern: "colonial", rows: 2, cols: 2 } : null };
  const p = 2, X1 = x + p, Y1 = y + p, X2 = x + w - p, Y2 = y + h - p;
  const out = [];
  if (HUNG.includes(node.type) && node.fill !== "solid") {  // meeting rail, sash lock, up/down arrows, grids per sash
    const [top] = hungSplit(node, h), ry = y + top, cx = x + w / 2, bh = h - top, on = node.grid_on || "both";
    const arrow = (y1, y2, k) => (
      <g key={k} data-testid={`design-hung-arrow-${k}-${idx}`}>
        <line x1={cx} y1={y1} x2={cx} y2={y2} stroke="#0071E3" strokeWidth="0.7" />
        <path d={`M ${cx} ${y2} l -1.6 ${y2 > y1 ? -2.4 : 2.4} l 3.2 0 z`} fill="#0071E3" />
      </g>
    );
    if (node.grid) {
      const t = node.grid.type || "btg";
      [["top", Y1, ry - 1], ["bottom", ry + 1, Y2]].filter(([k]) => on === "both" || on === k).forEach(([k, a, b]) => out.push(
        <g key={`grid-${k}`} clipPath={`url(#${clipId})`} data-testid={`design-grid-${idx}-${k}`} data-grid-type={t} data-grid-pattern={node.grid.pattern || "colonial"}>
          {gridBars(node.grid, X1, a, X2, b).map((bar, i) => <Bar key={i} b={bar} type={t} />)}
        </g>));
    }
    out.push(<line key="rail" data-testid={`design-hung-rail-${idx}`} x1={x} y1={ry} x2={x + w} y2={ry} stroke={INK} strokeWidth="1.2" />);
    out.push(<rect key="lock" x={cx - 2} y={ry - 1.6} width="4" height="1.1" fill={INK} />);
    out.push(arrow(ry + bh * 0.78, ry + bh * 0.3, "up"));
    if (node.type === "double_hung") out.push(arrow(y + top * 0.22, y + top * 0.7, "down"));
    return out;
  }
  if (node.fill === "solid") {
    out.push(<rect key="solid" data-testid={`design-solid-${idx}`} x={X1} y={Y1} width={Math.max(X2 - X1, 0)} height={Math.max(Y2 - Y1, 0)} fill="#D8D8DC" stroke={INK} strokeWidth="0.4" />);
    const ix = Math.min(w * 0.12, 4);
    out.push(<rect key="panel" x={X1 + ix} y={Y1 + ix} width={Math.max(X2 - X1 - 2 * ix, 0)} height={Math.max(Y2 - Y1 - 2 * ix, 0)} fill="none" stroke={INK} strokeWidth="0.35" />);
  } else if (node.grid) {
    const t = node.grid.type || "btg";
    out.push(<g key="grid" clipPath={`url(#${clipId})`} data-testid={`design-grid-${idx}`} data-grid-type={t} data-grid-pattern={node.grid.pattern || "colonial"}>
      {gridBars(node.grid, X1, Y1, X2, Y2).map((b, i) => (
        <Bar key={i} b={b} type={t} testId={node.grid.pattern === "custom" ? `design-bar-${idx}-${i}` : undefined} onDown={node.grid.pattern === "custom" && onBarDown ? onBarDown(i) : undefined} />
      ))}
    </g>);
    out.push(<text key="tag" x={X1 + 1} y={Y1 + 3.2} fontSize="2.4" fontWeight="700" fill={t === "btg" ? GOLD : INK}>{GRID_TAG[t]}</text>);
  }
  if (OPERABLE.includes(node.type) && node.type !== "sliding" && !HUNG.includes(node.type)) {
    const hx = node.hinge === "right" ? X1 + 2 : X2 - 2, cy = y + h / 2;
    out.push(node.type === "door" ? <circle key="knob" cx={hx} cy={cy} r="1.1" fill="none" stroke={INK} strokeWidth="0.4" />
      : <line key="handle" x1={hx} y1={cy - 2} x2={hx} y2={cy + 2} stroke={INK} strokeWidth="0.9" />);
  }
  return out;
}
